import { useState } from 'react';
import { PartialAccessNotice } from '../states/PartialAccessNotice.js';
import type { AnswerMessage, DrillAction } from '../types.js';
import { AnomalyCard } from './AnomalyCard.js';
import { ChartBlock } from './ChartBlock.js';
import { DrillChips } from './DrillChips.js';
import { KpiRow } from './KpiRow.js';
import { ResponseFooter } from './ResponseFooter.js';
import { ResultTable } from './ResultTable.js';
import { SourceCard } from './SourceCard.js';
import { DeepSearchCard } from './DeepSearchCard.js';
import { TextBlock, textPartsToString } from './TextBlock.js';

export interface AnswerViewProps {
  readonly message: AnswerMessage;
  readonly onDrill: (action: DrillAction, from: AnswerMessage) => void;
  readonly onFocusRecord: (label: string) => void;
  /** Newest answer? Only its record links move the context panel. */
  readonly isLatest: boolean;
  /** A new answer is being produced — drill controls wait. */
  readonly busy: boolean;
  /** A freshly produced answer: type the text out, then reveal the rest. */
  readonly typing?: boolean;
  /** Text grew — lets the stream keep the end of the answer in view. */
  readonly onProgress?: (() => void) | undefined;
}

/**
 * Renders one answer in the design's FIXED component order (frame F's soft
 * access notice, when present, sits above it):
 * text, KPI row, chart, result table, drill-down chips, source card,
 * anomaly, footer. Absent parts are simply skipped.
 */
export function AnswerView({
  message,
  onDrill,
  onFocusRecord,
  isLatest,
  busy,
  typing = false,
  onProgress,
}: AnswerViewProps) {
  const { content, footer } = message;
  const [typed, setTyped] = useState(!typing);
  const drill = (action: DrillAction) => onDrill(action, message);
  return (
    <div className="ai-answer" data-sample-id={message.sampleId}>
      {message.partialAccess ? <PartialAccessNotice access={message.partialAccess} /> : null}
      <TextBlock
        parts={content.text}
        typing={typing}
        onTyped={() => setTyped(true)}
        onProgress={onProgress}
      />
      {typed ? (
        <div className="ai-answer__rest">
          {content.kpis ? <KpiRow items={content.kpis} /> : null}
          {content.chart ? (
            <ChartBlock spec={content.chart} onDrill={drill} disabled={busy} />
          ) : null}
          {content.table ? (
            <ResultTable
              table={content.table}
              onDrill={drill}
              onFocusRecord={onFocusRecord}
              recordsInteractive={isLatest}
              disabled={busy}
            />
          ) : null}
          {content.drill ? (
            <DrillChips chips={content.drill} onDrill={drill} disabled={busy} />
          ) : null}
          {content.deepSearch ? <DeepSearchCard spec={content.deepSearch} /> : null}
          {content.source ? <SourceCard source={content.source} /> : null}
          {content.anomaly ? (
            <AnomalyCard anomaly={content.anomaly} onDrill={drill} disabled={busy} />
          ) : null}
          <ResponseFooter footer={footer} copyText={textPartsToString(content.text)} />
        </div>
      ) : null}
    </div>
  );
}
